'use client';
import { BarSegmentMetric, type BarSegmentMetricItem } from '@/components/metrics/BarSegmentMetric';
import { MetricHeader } from '@/components/metrics/MetricHeader';
import { CommissionRewardReviewSummaryNotice } from '@/components/orders/commission-reward-review-notice';
import { Card } from '@/components/ui';
import { Skeleton, VStack } from '@chakra-ui/react';
import type { CommissionStats as CommissionStatsType, StatsMetric } from '@fluenceur/types';
import { ComparisonPeriodType } from '@fluenceur/types';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface CommissionStatsProps {
  stats: CommissionStatsType;
  isLoading?: boolean;
}

export const CommissionStats: React.FC<CommissionStatsProps> = ({ stats, isLoading = false }) => {
  const { t } = useTranslation();

  // Transform total generated to StatsMetric format
  const totalGeneratedMetric: StatsMetric | undefined = useMemo(() => {
    if (!stats?.totalGenerated) return undefined;

    // The API sends two decimals; the dashboard trends carry one. Round once and
    // derive everything from that value, so a growth of 0.04 reads flat, not "+0%".
    // toFixed rounds the number received, as Intl.NumberFormat does; scaling by ten
    // first would round the float product instead (37.55 * 10 is 375.5, not 375.49...).
    const growth =
      stats.growthPercentage !== undefined ? Number(stats.growthPercentage.toFixed(1)) : undefined;

    // No periodDays on purpose: the comparison is the equal-length period right
    // before the selected range, which the comparison text names as such.
    const trend =
      growth !== undefined && stats.previousPeriodTotal
        ? {
            direction:
              growth > 0 ? ('up' as const) : growth < 0 ? ('down' as const) : ('flat' as const),
            percentage: Math.abs(growth),
            previousValue: stats.previousPeriodTotal.amount,
            label: `${growth > 0 ? '+' : ''}${growth}%`,
            periodType: ComparisonPeriodType.CUSTOM, // All stats now use custom date ranges
          }
        : undefined;

    return {
      key: 'totalCommissions',
      value: stats.totalGenerated.amount,
      formatted: new Intl.NumberFormat(t('common.locale'), {
        style: 'currency',
        currency: stats.totalGenerated.currencyCode,
      }).format(stats.totalGenerated.amount),
      currency: stats.totalGenerated.currencyCode,
      trend,
    };
  }, [stats, t]);

  // Transform commission data to bar segment items
  const barItems: BarSegmentMetricItem[] = useMemo(() => {
    const currency = stats?.totalGenerated?.currencyCode || 'EUR';
    const formatCurrency = (amount: number) =>
      new Intl.NumberFormat(t('common.locale'), {
        style: 'currency',
        currency,
      }).format(amount);

    return [
      {
        label: t('commissions.stats.paid'),
        value: stats?.totalPaid?.amount || 0,
        formattedValue: formatCurrency(stats?.totalPaid?.amount || 0),
        color: 'green.500',
      },
      {
        label: t('commissions.stats.unpaid'),
        value: stats?.totalPending?.amount || 0,
        formattedValue: formatCurrency(stats?.totalPending?.amount || 0),
        color: 'orange.emphasized',
      },
      {
        label: t('commissions.stats.processing'),
        value: stats?.totalProcessing?.amount || 0,
        formattedValue: formatCurrency(stats?.totalProcessing?.amount || 0),
        color: 'primary.500',
      },
    ];
  }, [stats, t]);

  return (
    <Card.Root>
      <Card.Body>
        <VStack alignItems="flex-start" gap={4}>
          <Skeleton height="60px" width="100%" loading={isLoading}>
            <MetricHeader title={t('commissions.stats.total')} metric={totalGeneratedMetric} />
          </Skeleton>

          <BarSegmentMetric items={barItems} isLoading={isLoading} />
          {!isLoading ? (
            <CommissionRewardReviewSummaryNotice
              summaries={stats.rewardReviews}
              showDescription={false}
            />
          ) : null}
        </VStack>
      </Card.Body>
    </Card.Root>
  );
};

export default CommissionStats;
